import i18next from 'i18next';
import parser from 'accept-language-parser';
import en from './locales/en/translation.json';
import es from './locales/es/translation.json';
import it from './locales/it/translation.json';
import de from './locales/de/translation.json';
import fr from './locales/fr/translation.json';
import pt from './locales/pt/translation.json';
import ja from './locales/ja/translation.json';
import en_GOV from './locales/en-GOV/translation.json';
import './types/i18next';
export { en, es, it, de, fr, pt, ja, en_GOV };
export const EN_GOV = 'en-GOV';
export const EN = 'en';
const DEFAULT_LANGUAGE = EN;
/**
 * Add new language files here as they are needed
 */
export const RESOURCES = { en, es, it, de, fr, pt, ja, en_GOV };
/**
 * Formatted version of RESOURCES for use with i18next
 */
export const I18NEXT_RESOURCES = Object.fromEntries(Object.entries(RESOURCES).map(([key, value]) => [
    // en-GOV is not a valid JS variable name, so we use _.
    // In order to find the correct language code, we need to convert to - in the map
    key.replace('_', '-'),
    { translation: value },
]));
/**
 * Standard i18n instance useful for backend applications
 */
export async function initializeI18n(defaultLanguage = DEFAULT_LANGUAGE) {
    return i18next.init({
        lng: defaultLanguage,
        fallbackLng: defaultLanguage,
        resources: I18NEXT_RESOURCES,
        interpolation: {
            escapeValue: false, // not needed on backend
        },
    });
}
/**
 * Provides the same t function as normal but centralizes the language detection
 */
export function getTFunction(i18n, acceptLanguageProvider) {
    return ((...args) => {
        const key = args[0];
        const defaultValue = typeof args[1] === 'string' ? args[1] : '';
        // Options can either be the 2nd or 3rd argument
        const options = typeof args[1] === 'object'
            ? args[1]
            : typeof args[2] === 'object'
                ? args[2]
                : {};
        return i18n(key, defaultValue, {
            ...options,
            lng: getPreferredLanguage(acceptLanguageProvider()),
        });
    });
}
/**
 * This function is used to mark a string as an i18n key so it is picked up by the extract process.
 * This can be used in places where a message is passed to the UI from the backend/different package.
 * Rather than passing the t function everywhere, we can just use it on this key where the text needs
 * to be displayed
 *
 * @returns an array of [key, defaultValue, values]. With this, you can call `t(...msg)`
 */
export function tKey(key, values) {
    // empty string is the "default" value; this is just so this always matches the correct t function signature
    return [key, '', values ?? {}];
}
/**
 * Convenience function to translate a Zod issue
 */
export function tZodIssue(issue, t) {
    if (issue.code === 'custom') {
        return t(issue.message, '', issue.params);
    }
    return '';
}
export function getPreferredLanguage(acceptLanguageHeader) {
    const availableLanguages = Object.keys(I18NEXT_RESOURCES);
    if (!acceptLanguageHeader)
        return DEFAULT_LANGUAGE;
    try {
        // Parse the Accept-Language header
        const parsedLanguages = parser.parse(acceptLanguageHeader);
        for (const parsed of parsedLanguages) {
            // First, try exact match
            const full = `${parsed.code}-${parsed.region}`;
            if (availableLanguages.includes(full)) {
                return full;
            }
            // Then try base language match (e.g., 'en-US' -> 'en')
            if (availableLanguages.includes(parsed.code)) {
                return parsed.code;
            }
        }
        return DEFAULT_LANGUAGE;
    }
    catch (error) {
        return DEFAULT_LANGUAGE;
    }
}
export const getTMessageForEnum = (enumValue) => {
    // turn enum in snake case to camel case, ie: 'workout_template' -> 'workoutTemplate'
    const camelCaseKey = enumValue
        .split('_')
        .map((word, index) => index === 0
        ? word.toLowerCase()
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join('');
    return tKey(camelCaseKey);
};
//# sourceMappingURL=index.js.map